import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component, Input, OnChanges, OnInit,
  SimpleChanges
} from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { PillComponent } from '@sondermind/features/pill';
import { SessionType } from '@sondermind/utilities/models-session';

const MAX_NUMBER_OF_SPECIALTY_PILLS = 4;

@Component({
  selector: 'provider-pills',
  imports: [
    CommonModule,
    PillComponent,
  ],
  templateUrl: './provider-pills.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./provider-pills.component.scss']
})
export class ProviderPillsComponent implements OnInit, OnChanges {
  // Provider data needed to handle pill generation
  @Input() providerAcceptedInsuranceIds: number[] | null = null;
  @Input() providerInsurances: string[] | null = null;
  @Input() providerSessionTypesOffered: string[] = [];
  @Input() providerSpecialties: Array<{
    id: number;
    name: string;
    description: string;
  }> | null = null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @Input() providerTopSpecialtiesIds: number[] | null = null;

  // The user's chosen insurance and/or specialties
  @Input() selectedInsurancePncId: string | null = null;
  @Input() selectedInsuranceName: string | null = null;
  @Input() selectedSpecialties: string[] = [];

  @Input() longSessionTypesDisplayText: boolean = false;

  specialtyNames: BehaviorSubject<string[]> = new BehaviorSubject<string []>([]);
  insuranceName: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  offersVideo: boolean = false;
  offersInPerson: boolean = false;

  ngOnInit(): void {
    this.getSessionTypes();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedInsurancePncId'] || changes['selectedInsuranceName']) {
      this.insuranceName.next(this.getSelectedInsuranceName());
    }
    if (changes['selectedSpecialties']) {
      this.specialtyNames.next(this.getSelectedSpecialtiesNames());
    }
  }

  private getSessionTypes(): void {
    this.offersInPerson = this.providerSessionTypesOffered.includes(SessionType.IN_PERSON);
    this.offersVideo = this.providerSessionTypesOffered.includes(SessionType.VIDEO);
  }

  private getSelectedSpecialtiesNames(): string[] {
    const result: string[] = [];

    if (this.selectedSpecialties?.length) {
      // If we are given the provider's specialties and top specialties to filter by
      if (this.providerSpecialties && this.providerTopSpecialtiesIds) {
        this.providerSpecialties.forEach((specialty) => {
          if (result.length < MAX_NUMBER_OF_SPECIALTY_PILLS
            && this.selectedSpecialties.includes(specialty.id.toString())
            // we highlight top specialties differently, so don't add a pill
            && !this.providerTopSpecialtiesIds?.includes(specialty.id)
          ) {
            result.push(specialty.name);
          }
        });
      } else {
        // We have been given a set of pre-filtered specialties
        result.push(...this.selectedSpecialties);
      }
    }

    return result;
  }

  private getSelectedInsuranceName(): string | null {
    if (this.selectedInsurancePncId
      && this.providerAcceptedInsuranceIds?.includes(Number(this.selectedInsurancePncId))
    ) {
      return this.selectedInsuranceName;
    }

    // If we have a insurance name but no accepted ids, we can assume that the
    // insurance is already accepted by the provider (in the directory)
    if (this.selectedInsuranceName && !this.providerAcceptedInsuranceIds) {
      return this.selectedInsuranceName;
    }

    return null;
  }
}
